// ═══ 검차 신청 (B1~B6) — ① 입력 방식 · ② 차량 정보 · ③ 위치/확인 + 사진·링크 단일 화면 ═══
// 앱 simple_request_flow_screen.dart 1:1 (신고 371 N). 화면 상태(mode/step/ocr/폼)는 여기,
// 제출 배선은 request/useRequestSubmit.js, 순수 로직(정규화·FormData·중복·OCR 프리필)은 request/formLogic.js,
// 화면 조각은 screens/request/* 가 렌더만 한다.
//
//   mode  none | photo | link | manual      step 1|2|3      ocr idle|loading|filled|failed
//
// 규칙: 업로드는 api.createOrder(FormData) 경유(직접 fetch 금지) · 사진은 클라에서 3장 선제한
// (서버가 4장을 받으면 요청 전체가 400 INVALID_PHOTO) · 서버 한글 message·code 문자열은 노출 금지.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T, R } from '../../theme';
import { useT } from '../../../i18n/I18nProvider';
import { useIsMobile } from '../../../utils/useMobile';
import { useIsDesktop } from '../useDesktop';
import { ocrHeydealer, feeDefault, listOrders } from '../api';
import ErrorText from '../parts/ErrorText';
import { card } from './request/bits';
import RequestStepper from './request/RequestStepper';
import StepMethod from './request/StepMethod';
import StepPhotos from './request/StepPhotos';
import StepLink from './request/StepLink';
import StepVehicle from './request/StepVehicle';
import StepLocation from './request/StepLocation';
import DupDialog from './request/DupDialog';
import MapPickModal from './request/MapPickModal';
import AddrPickModal from './request/AddrPickModal';
import { buildOcrNotes } from './request/ocrNotes';
import DesktopForm from './request/DesktopForm';
import DesktopSummary from './request/DesktopSummary';
import { DEFAULT_FEE, EMPTY_FORM, MAX_PHOTOS, hostOf, normalizeListingUrl, ocrPrefill } from './request/formLogic';
import useRequestSubmit from './request/useRequestSubmit';

var page = { display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720, margin: '0 auto' };
var backRow = { display: 'flex', alignItems: 'center', gap: 8 };
var backBtn = { border: 0, background: 'none', padding: 0, cursor: 'pointer', color: T.muted, fontSize: 18, lineHeight: 1, fontFamily: 'inherit' };
var title = { fontSize: 19, fontWeight: 800, letterSpacing: '-.35px', color: T.navy };
var toastSt = { position: 'fixed', top: 20, insetInlineStart: '50%', transform: 'translateX(-50%)', background: T.ok,
  color: T.card, padding: '10px 24px', borderRadius: R.card, fontSize: 14, fontWeight: 600, zIndex: 9995, boxShadow: T.shadowMenu };

function SimpleNewRequest() {
  var t = useT();
  var navigate = useNavigate();
  var isMobile = useIsMobile();
  var isDesktop = useIsDesktop();   // ≥1024 = 1페이지 2열 뷰(상태·로직은 아래 그대로 공유)
  var sp = useSearchParams(); var searchParams = sp[0];

  // 취소 사유 카드의 "다시 신청" — 차종·차량번호를 query 로 이어받는다 (BUILD_PLAN §5·§6).
  var prefillModel = searchParams.get('model') || '';
  var prefillPlate = searchParams.get('plate') || '';
  var hasPrefill = !!(prefillModel || prefillPlate);

  var stMode = useState(hasPrefill ? 'manual' : 'none'); var mode = stMode[0], setMode = stMode[1];
  var stStep = useState(hasPrefill ? 2 : 1); var step = stStep[0], setStep = stStep[1];
  var stOcr = useState('idle'); var ocr = stOcr[0], setOcr = stOcr[1];
  var stChoice = useState('ai'); var choice = stChoice[0], setChoice = stChoice[1];
  var stPhotos = useState([]); var photos = stPhotos[0], setPhotos = stPhotos[1];
  var stForm = useState(hasPrefill ? Object.assign({}, EMPTY_FORM, { car_model: prefillModel, car_number: prefillPlate }) : EMPTY_FORM);
  var form = stForm[0], setForm = stForm[1];
  var stErr = useState({}); var errors = stErr[0], setErrors = stErr[1];
  var stFee = useState(DEFAULT_FEE); var fee = stFee[0], setFee = stFee[1];
  var stOrders = useState([]); var orders = stOrders[0], setOrders = stOrders[1];
  var stClip = useState(''); var clipUrl = stClip[0], setClipUrl = stClip[1];
  var stPick = useState(''); var pick = stPick[0], setPick = stPick[1];
  var stNotes = useState(''); var ocrNotes = stNotes[0], setOcrNotes = stNotes[1];

  var fileRef = useRef(null);
  var seqRef = useRef(0);
  var urlsRef = useRef([]);
  var timerRef = useRef(null);

  // 기본 검차비 + 중복 선검사용 목록. 실패는 조용히 — 신청 자체를 막지 않는다.
  useEffect(function () {
    var alive = true;
    feeDefault().then(function (r) {
      var base = r && r.data && Number(r.data.baseFee);
      if (alive && base > 0) setFee(base);
    }).catch(function () { /* 기본값 유지 */ });
    listOrders({}).then(function (r) {
      if (alive && r && Array.isArray(r.data)) setOrders(r.data);
    }).catch(function () { /* 선검사만 건너뛴다 */ });
    return function () { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function () {
    var urls = urlsRef.current;
    var timers = timerRef;
    return function () {
      if (timers.current) window.clearTimeout(timers.current);
      urls.forEach(function (u) {
        try { window.URL.revokeObjectURL(u); } catch (e) { /* 이미 해제 */ }
      });
    };
  }, []);

  var previewUrl = function (file) {
    try {
      if (window.URL && window.URL.createObjectURL) {
        var u = window.URL.createObjectURL(file);
        urlsRef.current.push(u);
        return u;
      }
    } catch (e) { /* 미지원 브라우저 — 파일명만 보여준다 */ }
    return '';
  };

  var setField = function (key, value) {
    setForm(function (prev) {
      var next = Object.assign({}, prev);
      next[key] = value;
      return next;
    });
  };

  // ── ① 입력 방식 ────────────────────────────────────────────────────────
  var openPicker = function () { if (fileRef.current) fileRef.current.click(); };

  var addFiles = function (list) {
    var picked = Array.prototype.slice.call(list || []);
    if (!picked.length) return;
    var room = Math.max(MAX_PHOTOS - photos.length, 0);
    var take = picked.slice(0, room);
    var added = take.map(function (file) {
      seqRef.current += 1;
      return { key: 'p' + seqRef.current, file: file, name: file.name || 'photo.jpg', url: previewUrl(file) };
    });
    setPhotos(photos.concat(added));
    // 4장째부터는 잘라내고 안내 — 서버가 4장을 받으면 요청 전체가 400 이라 부분 성공이 없다.
    setErrors(picked.length > take.length ? { photo: t('simpleB4TooMany') } : {});
    setMode('photo');
    setStep(1);
  };

  var onFiles = function (e) {
    var list = (e.target && e.target.files) || [];
    var picked = Array.prototype.slice.call(list);
    if (e.target) e.target.value = '';
    addFiles(picked);
  };

  var openLink = function () {
    setMode('link');
    setErrors({});
    // 결정 13 — 클립보드 권한이 없거나 거부되면 pill 을 띄우지 않는다.
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText().then(function (text) {
          var u = normalizeListingUrl(text);
          if (u) setClipUrl(u);
        }).catch(function () { /* 권한 없음 — pill 숨김 */ });
      }
    } catch (e) { /* 구형 브라우저 */ }
  };

  var openManual = function () {
    setMode('manual');
    setOcr('idle');
    setErrors({});
    setStep(2);
  };

  // 홈 3택 스트립 → ?method=photo|link|manual : 그 방식이 선택된 상태로 연다 (마운트 1회).
  var methodOnce = useRef(false);
  useEffect(function () {
    if (methodOnce.current) return;
    methodOnce.current = true;
    var m = searchParams.get('method');
    if (m === 'link') openLink();
    else if (m === 'manual') openManual();
    else if (m === 'photo') { setMode('photo'); setStep(1); setErrors({}); }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ② 첨부 화면 ────────────────────────────────────────────────────────
  var removePhoto = function (index) {
    setPhotos(photos.filter(function (p, i) { return i !== index; }));
  };

  var runOcr = function () {
    if (!photos.length) { setErrors({ photo: t('simpleB4NeedPhoto') }); return; }
    setErrors({});
    setOcr('loading');
    ocrHeydealer(photos.map(function (p) { return p.file; })).then(function (res) {
      var d = (res && res.data) || {};
      setForm(function (prev) { return ocrPrefill(prev, d); });
      setOcrNotes(buildOcrNotes(d));
      setOcr('filled');
      setStep(2);   // 사진은 photos 에 남아 최종 제출에 붙는다
    }).catch(function () {
      setOcr('failed');   // 서버 문구 노출 금지 — 앰버 블록 안내만
    });
  };

  var manualFromPhotos = function () {
    setOcr('idle');
    setStep(2);
  };

  // ── 제출 (request/useRequestSubmit.js — runOcr 이후에 불러야 submitChoice 가 그것을 집는다) ──
  var sub = useRequestSubmit({ t: t, navigate: navigate, mode: mode, ocr: ocr, choice: choice,
    form: form, photos: photos, orders: orders, ocrNotes: ocrNotes,
    setErrors: setErrors, setStep: setStep, runOcr: runOcr, timerRef: timerRef });

  // ── 위치 선택 ──────────────────────────────────────────────────────────
  var openAddr = function () {
    // Daum Postcode 스크립트가 없으면(차단·오프라인) 직접 입력 칸으로 폴백.
    if (window.daum && window.daum.Postcode) { setPick('addr'); return; }
    var el = document.getElementById('req-location');
    if (el) el.focus();
  };

  var applyLocation = useCallback(function (address) {
    setPick('');
    if (!address) return;
    setField('location', address);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 이동 ───────────────────────────────────────────────────────────────
  var goBack = function () {
    setErrors({});
    if (mode === 'link') { setMode('none'); return; }
    if (mode === 'photo' && step === 1) {
      setPhotos([]);
      setOcr('idle');
      setChoice('ai');
      setMode('none');
      return;
    }
    if (step > 1) { setStep(step - 1); return; }
    navigate('/main/s/home');
  };

  var methodLabel = mode === 'photo' ? t('simpleOcrTitle') : mode === 'link' ? t('simpleB3LinkTitle') : t('simpleManualTitle');
  var photoScreen = mode === 'photo' && step === 1;
  var showStepper = mode !== 'link' && !photoScreen;
  var cardStyle = Object.assign({}, card, { padding: isMobile ? 14 : 18 });

  var overlays = (<React.Fragment>
    <input ref={fileRef} type="file" accept="image/*" multiple data-testid="photo-input"
      style={{ display: 'none' }} onChange={onFiles} />

    <DupDialog t={t} open={!!sub.dup}
      title={sub.dup && sub.dup.kind === 'link' ? t('simpleB3LinkDupTitle') : t('simpleDupTitle')}
      sub={sub.dup && sub.dup.kind === 'link' ? t('simpleB3LinkDupSub') : t('simpleDupSub')}
      onClose={sub.closeDup} onView={sub.viewDup} />
    <MapPickModal t={t} open={pick === 'map'} initial={form.location}
      onPick={applyLocation} onClose={function () { setPick(''); }} />
    <AddrPickModal t={t} open={pick === 'addr'} onPick={applyLocation} onClose={function () { setPick(''); }} />

    {sub.toast ? <div style={toastSt} role="status">{sub.toast}</div> : null}
  </React.Fragment>);

  // ── 데스크톱(≥1024) — 같은 상태를 1페이지 2열로 펼친다. 모바일·769~1023 은 아래 3단계 그대로.
  if (isDesktop) {
    return (<div className="ss-side">
      <DesktopForm t={t} mode={mode} form={form} photos={photos} errors={errors} ocr={ocr} choice={choice}
        clipboardHost={clipUrl ? hostOf(clipUrl) : ''} onBack={function () { navigate('/main/s/home'); }}
        onPhoto={openPicker} onLink={openLink} onManual={openManual}
        onAdd={openPicker} onDropFiles={addFiles} onRemove={removePhoto} onChoice={setChoice}
        onRunOcr={runOcr} onManualEntry={manualFromPhotos} onChange={setField}
        onPasteClipboard={function () { setField('listing_url', clipUrl); setClipUrl(''); setErrors({}); }}
        onPickMap={function () { setPick('map'); }} onPickAddr={openAddr}>
        {overlays}
      </DesktopForm>
      <div className="ss-sticky">
        <DesktopSummary t={t} form={form} photos={photos} errors={errors} submitError={sub.submitError} fee={fee}
          listingHost={hostOf(normalizeListingUrl(form.listing_url))}
          disabled={sub.submitting || mode === 'none'} onSubmit={sub.submitDesktop} />
      </div>
    </div>);
  }

  return (<div style={page}>
    <div style={backRow}>
      <button type="button" style={backBtn} aria-label={t('actionBack')} data-testid="req-back" onClick={goBack}>‹</button>
      <h2 style={title}>{t('simpleReqTitle')}</h2>
    </div>

    <div style={cardStyle}>
      {showStepper ? <RequestStepper t={t} step={step} /> : null}

      {mode === 'link' ? (<StepLink t={t} isMobile={isMobile} url={form.listing_url} memo={form.memo}
        urlError={errors.url} clipboardHost={clipUrl ? hostOf(clipUrl) : ''} submitting={sub.submitting}
        onUrl={function (v) { setField('listing_url', v); }}
        onPasteClipboard={function () { setField('listing_url', clipUrl); setClipUrl(''); setErrors({}); }}
        onMemo={function (v) { setField('memo', v); }} onSubmit={sub.submitLink} />) : null}

      {photoScreen ? (<StepPhotos t={t} isMobile={isMobile} photos={photos} choice={choice} ocr={ocr}
        memo={form.memo} error={errors.photo} submitting={sub.submitting}
        onAdd={openPicker} onRemove={removePhoto} onChoice={setChoice}
        onMemo={function (v) { setField('memo', v); }} onSubmit={sub.submitChoice}
        onManualEntry={manualFromPhotos} onRetry={runOcr} />) : null}

      {!photoScreen && mode !== 'link' && step === 1 ? (<StepMethod t={t}
        onPhoto={openPicker} onLink={openLink} onManual={openManual} />) : null}

      {!photoScreen && mode !== 'link' && step === 2 ? (<StepVehicle t={t} isMobile={isMobile} form={form}
        onChange={setField} modelError={errors.model} ocrFilled={ocr === 'filled'} photoCount={photos.length}
        plateMissing={!form.car_number.trim()} onBack={goBack} onNext={sub.nextFromVehicle} />) : null}

      {!photoScreen && mode !== 'link' && step === 3 ? (<StepLocation t={t} isMobile={isMobile} form={form}
        onChange={setField} locationError={errors.location} urlError={errors.url} fee={fee}
        methodLabel={methodLabel} listingHost={hostOf(normalizeListingUrl(form.listing_url))}
        submitting={sub.submitting} onPickMap={function () { setPick('map'); }} onPickAddr={openAddr}
        onBack={goBack} onSubmit={sub.submitFull} />) : null}

      <ErrorText>{sub.submitError}</ErrorText>
    </div>

    {overlays}
  </div>);
}

export default SimpleNewRequest;
